/* eslint-disable no-shadow */
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import apis from '@src/apis';
import { REQUEST_STATUS } from '@src/constants/voice';
import { FETCH_REQUESTS_INTERVAL } from '@src/constants/websocket';
import NotificationDialogV2 from '@src/components/NotificationDialogV2';
import route from '@src/constants/route';
import EmptyRequest from './EmptyRequest';
import Requests from './Requests';
import { StyledLoading } from './index.style';

const FIELDS =
  'id,title,characters,credits,createdAt,progress,status,voice,audioType,audioLink,retentionPeriod,processingAt,endedAt,deviceInfo';

const MobileTTSRequests = () => {
  const { t } = useTranslation();

  const PAGINATION_LIMIT = 20;
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [openWrongSyntaxDialogV2, setOpenWrongSyntaxDialogV2] = useState(null);
  const [page, setPage] = useState(1);
  const requestsRef = useRef(requests);
  const { synthesisRequest } = useSelector((state) => state.synthesisRequest);
  const { languages } = useSelector((state) => state.language);
  // Show dialog block download cause user already download
  const { showBlockDownloadDialog } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const history = useHistory();

  const fetchRequests = async (page, hasLoading = false) => {
    if (hasLoading) setLoading(true);
    const newParams = {
      offset: (page - 1) * PAGINATION_LIMIT,
      limit: PAGINATION_LIMIT,
      sort: 'createdAt_desc',
      fields: FIELDS,
    };
    const data = await apis.requests.getRequestsV2(newParams);

    setLoading(false);
    if (data && data.status) {
      const newRequests = data.result.requests.map((request) => {
        const req = requests.find((r) => r.id === request.id);
        if (req) {
          return {
            ...request,
            progress:
              req?.progress && request.progress < req?.progress
                ? req.progress
                : request.progress,
          };
        }
        return request;
      });

      if (newRequests.length < newParams.limit) {
        setHasMore(false);
      }
      setRequests((prevRequests) => [
        ...prevRequests,
        ...newRequests.filter(
          (newRequest) => !prevRequests.some((req) => req.id === newRequest.id),
        ),
      ]);
    }
  };

  const updateCurrentSynthesisRequest = (listRequest) => {
    const currentRequest = listRequest.find(
      (obj) => obj.id === synthesisRequest.id,
    );

    if (currentRequest) {
      const { voice } = currentRequest;
      let voiceLanguage = languages.find(
        (lang) => lang?.value === voice?.languageCode,
      );

      voiceLanguage = { ...voiceLanguage, code: voiceLanguage?.value };
      dispatch(
        actions.synthesisRequest.updateSynthesisConfig({
          ...synthesisRequest,
          ...currentRequest,
          voice: { ...currentRequest.voice, language: voiceLanguage },
        }),
      );
    }
  };

  const handleUpdateRequests = (newRequests) => {
    setRequests(newRequests);
    updateCurrentSynthesisRequest(newRequests);
  };

  const fetchProgressRequest = async (requestId) => {
    const data = await apis.requests.getProgressRequest(requestId);
    if (data && data.status) {
      const newRequests = requestsRef.current.map((request) => {
        if (request.id === requestId) {
          const { progress, status, audioLink, endedAt, processingAt } =
            data.result;
          return {
            ...request,
            progress,
            status,
            audioLink,
            processingAt: processingAt || request.processingAt,
            endedAt: endedAt || request.endedAt,
          };
        }
        return request;
      });
      handleUpdateRequests(newRequests);
    }
  };

  const updateRequest = (requestId, data) => {
    setRequests((prevRequests) =>
      prevRequests.map((request) =>
        request.id === requestId ? { ...request, ...data } : request,
      ),
    );
  };

  const deleteRequest = (requestId) => {
    setRequests((prevRequests) =>
      prevRequests.filter((request) => request.id !== requestId),
    );
  };

  const updateInprogressRequest = (requestIds) => {
    Promise.all(
      requestIds.map(async (requestId) => {
        await fetchProgressRequest(requestId);
        return null;
      }),
    );
  };

  const renderBuyPackageButton = () => (
    <Button
      className="btn-understood"
      variant="contained"
      onClick={() => history.push(route.PAYMENT)}
    >
      {t('upgradeNow')}
    </Button>
  );

  useEffect(() => {
    fetchRequests(page, true);
  }, [page]);

  useEffect(() => {
    requestsRef.current = requests;
  }, [requests]);

  useEffect(() => {
    const fetchRequestsInterval = setInterval(async () => {
      const inProgressRequest = requestsRef.current
        .filter((request) => request.status === REQUEST_STATUS.IN_PROGRESS)
        .map((request) => request.id);
      if (inProgressRequest?.length) updateInprogressRequest(inProgressRequest);
    }, FETCH_REQUESTS_INTERVAL);

    return () => clearInterval(fetchRequestsInterval);
  }, [synthesisRequest?.id]);

  useEffect(() => {
    if (showBlockDownloadDialog) {
      setOpenWrongSyntaxDialogV2({
        name: 'error-syntax',
        variant: 'warning',
        title: t('unableToDownload'),
        description: t('unableToDownloadDescription', { numberDownload: 1 }),
        actionComponent: renderBuyPackageButton(),
      });
    }
  }, [showBlockDownloadDialog]);

  if (loading && requests.length === 0) {
    return (
      <StyledLoading>
        <CircularProgress />
      </StyledLoading>
    );
  }

  return requests?.length ? (
    <>
      <Requests
        hasMore={hasMore}
        requests={requests}
        loading={loading}
        setLoading={setLoading}
        setPage={setPage}
        updateRequest={updateRequest}
        deleteRequest={deleteRequest}
      />
      <NotificationDialogV2
        name={openWrongSyntaxDialogV2?.name}
        title={openWrongSyntaxDialogV2?.title}
        description={openWrongSyntaxDialogV2?.description}
        subDescription={openWrongSyntaxDialogV2?.subDescription}
        variant={openWrongSyntaxDialogV2?.variant}
        open={!!openWrongSyntaxDialogV2?.title}
        onClose={() => {
          dispatch(actions.user.updateShowBlockDownloadDialog(false));
          setOpenWrongSyntaxDialogV2(null);
        }}
        actionComponent={openWrongSyntaxDialogV2?.actionComponent}
      />
    </>
  ) : (
    <EmptyRequest />
  );
};

export default MobileTTSRequests;
